// Revenue analytics workspace — /analytics/revenue. Answers "how much did this
// facility make over the selected window, and how did it trend day by day?"
//
// THIN presentation layer: every number comes from a backend field. The ONLY
// client-side math is Avg Revenue/Day = period revenue ÷ number of days, derived
// with parseDecimal over the selected view's explicit day span.
//
// Backing data (engineer-added clients in libs/analyticsApi.ts):
//   getFacilityRevenue(id, start, end)      -> { revenue, currency }
//   getFacilityBookingCount(id, start, end) -> { booking_count }
//   getFacilityTrends(id, period)           -> { data_points: [{date,revenue,
//                                                                booking_count}] }
//
// Conventions mirror operator-dashboard.tsx: useResolvedFacility gate (#883),
// plain useEffect + useState + Promise.allSettled (no react-query for facility
// data), native MUI presentation, shared money helpers. Revenue by service is
// backed by the canonical gross/refund/fee projection, not frontend accounting.

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { formatMoney, parseDecimal } from "@bw/shared/utils";
import { useEffect, useMemo, useState, type ElementType } from "react";

import {
  getFacilityRevenue,
  getFacilityRevenueBySource,
  getFacilityBookingCount,
  getFacilityTrends,
  exportFacilityTransactionsCsv,
  type FacilityRevenueResponse,
  type FacilityRevenueBySourceResponse,
  type FacilityBookingCountResponse,
  type FacilityTrendsResponse,
} from "@/libs/analyticsApi";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { PeriodSelector, type AnalyticsPeriod } from "./period-selector";
import { rangeForPeriod, daysInPeriod } from "./period-range";
import { finiteOrNull } from "./finite-field";
import { RevenueBarChart, type RevenueBarPoint } from "./revenue-bar-chart";

const PLACEHOLDER = "—";
const SOURCE_LABELS: Record<string, string> = {
  court: "Court reservations",
  drop_in: "Drop-in",
  program: "Classes",
  lesson: "Coaching",
  membership: "Memberships",
  tournament: "Tournaments",
  other: "Other",
};

function moneyLabel(
  value: number | string | null | undefined,
  currency: string | null | undefined,
) {
  return currency ? formatMoney(value, currency) : PLACEHOLDER;
}

// Short day label for a trend point (e.g. "Jun 8"). Wrap the ISO date in a Date
// pinned to local midnight so it doesn't drift a day in negative-offset zones.
function dayLabel(date: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(`${date}T00:00:00`));
}

function Kpi({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: ElementType;
}) {
  return (
    <Paper sx={{ p: 1.5 }} variant="outlined">
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <Box
          sx={{
            bgcolor: "action.selected",
            borderRadius: 2,
            color: "primary.main",
            display: "grid",
            flexShrink: 0,
            height: 40,
            placeItems: "center",
            width: 40,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}
            variant="h6"
          >
            {value}
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {label}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export function RevenueAnalyticsWorkspace() {
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();

  const [period, setPeriod] = useState<AnalyticsPeriod>("week");

  const [revenue, setRevenue] = useState<FacilityRevenueResponse | null>(null);
  const [bookingCount, setBookingCount] =
    useState<FacilityBookingCountResponse | null>(null);
  const [trends, setTrends] = useState<FacilityTrendsResponse | null>(null);
  const [sources, setSources] =
    useState<FacilityRevenueBySourceResponse | null>(null);
  const [sourcesError, setSourcesError] = useState(false);
  const [loading, setLoading] = useState(true);
  // A failed fetch must NEVER fabricate $0.00 / a count — gate into an honest
  // "—" / banner via these error flags.
  const [revenueError, setRevenueError] = useState<string | null>(null);
  const [trendsError, setTrendsError] = useState<string | null>(null);

  // "Export transactions" action state. Kept separate from the KPI errors so an
  // export failure surfaces its OWN small inline message and never blanks the
  // page. `exporting` disables the button so a double-click can't double-trigger.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    // Gate every read on a truthy, org-verified facilityId (#883). No facility
    // resolved yet → ZERO api calls.
    if (!facilityId) return;
    let cancelled = false;
    const { start, end } = rangeForPeriod(period);
    setLoading(true);
    setRevenueError(null);
    setTrendsError(null);
    setSourcesError(false);
    void (async () => {
      // Three independent reads — one failing must not blank the others, and
      // none may invent a number. Settle all.
      const [revenueResult, countResult, trendsResult, sourcesResult] =
        await Promise.allSettled([
          getFacilityRevenue(facilityId, start, end),
          getFacilityBookingCount(facilityId, start, end),
          getFacilityTrends(facilityId, period, end),
          getFacilityRevenueBySource(facilityId, start, end),
        ]);
      if (cancelled) return;
      setSources(
        sourcesResult.status === "fulfilled" ? sourcesResult.value : null,
      );
      setSourcesError(sourcesResult.status === "rejected");

      if (revenueResult.status === "fulfilled") {
        setRevenue(revenueResult.value);
      } else {
        setRevenue(null);
        setRevenueError(
          revenueResult.reason instanceof Error
            ? revenueResult.reason.message
            : "Couldn't load revenue.",
        );
      }

      if (countResult.status === "fulfilled") {
        setBookingCount(countResult.value);
      } else {
        setBookingCount(null);
      }

      if (trendsResult.status === "fulfilled") {
        setTrends(trendsResult.value);
      } else {
        setTrends(null);
        setTrendsError(
          trendsResult.reason instanceof Error
            ? trendsResult.reason.message
            : "Couldn't load daily revenue.",
        );
      }

      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId, period]);

  // Daily revenue bars — one per backend trend point. `revenue` is an unenforced
  // Decimal (#1340): drop any point whose revenue does not parse to a finite
  // number rather than fabricate a $0 bar from garbage (parseDecimal(garbage)
  // ?? 0 would plot a misleading zero).
  const barData = useMemo<RevenueBarPoint[]>(() => {
    const points: RevenueBarPoint[] = [];
    for (const point of trends?.data_points ?? []) {
      const value = finiteOrNull(parseDecimal(point?.revenue));
      if (value === null) continue;
      points.push({ label: dayLabel(point.date), value });
    }
    return points;
  }, [trends?.data_points]);

  // Avg Revenue/Day = period revenue ÷ number of days in the selected window.
  // The denominator is the period's day-span (today=1, week=7, month=30), so the
  // average is stable for the window the operator chose rather than skewed by how
  // many trend buckets the backend happened to return. The ONLY client-side math
  // on this page; null on any missing input so it never prints NaN or a
  // fabricated figure.
  const avgPerDay = useMemo<string>(() => {
    const total = parseDecimal(revenue?.revenue);
    const days = daysInPeriod(period);
    if (total === null || days <= 0) return PLACEHOLDER;
    return moneyLabel(total / days, revenue?.currency);
  }, [revenue?.revenue, revenue?.currency, period]);

  // Export the transactions CSV for the CURRENTLY selected window. The fetch is
  // the load-bearing action; offering it as a download is best-effort (jsdom
  // lacks URL.createObjectURL), so that part is wrapped in its own try/catch and
  // a failure there is swallowed — it must NOT raise the export error banner.
  // The CSV is offered VERBATIM (no parsing/rebuilding) — a thin presentation
  // layer. A client REJECTION (e.g. 403/500) sets an honest inline error that
  // never echoes the raw backend message or any uuid.
  async function handleExport() {
    if (!facilityId || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const { start, end } = rangeForPeriod(period);
      const csv = await exportFacilityTransactionsCsv(facilityId, start, end);
      // `url` is declared OUTSIDE the try so it can be revoked in the finally
      // even if anchor.click() throws (real browsers can throw "download
      // blocked" / "user gesture required") — otherwise the object URL leaks
      // for the page's lifetime.
      let url: string | undefined;
      try {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = "transactions.csv";
        anchor.click();
      } catch {
        // Best-effort download only — the export itself succeeded. Swallow a
        // jsdom/createObjectURL failure rather than show a misleading error.
      } finally {
        // Revoke whatever URL was created, regardless of how the download went.
        // No-op when createObjectURL itself threw (url stays undefined).
        if (url) URL.revokeObjectURL(url);
      }
    } catch {
      setExportError("Couldn't export transactions.");
    } finally {
      setExporting(false);
    }
  }

  // Brief resolve window before the FacilityContext selection lands. The
  // Layer-3 gate owns the genuine "no facility" case, so we just show loading.
  if (facilityLoading || !facilityId) {
    return (
      <Stack
        aria-label="Loading revenue analytics"
        role="status"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  const periodRevenue =
    revenue !== null
      ? moneyLabel(revenue.revenue, revenue.currency)
      : PLACEHOLDER;
  // Bookings — booking_count is unenforced (#1340), so gate it to a finite
  // number BEFORE stringifying; String(null)/String(undefined) would leak the
  // literal "null"/"undefined" to the operator. A non-numeric count degrades
  // to an honest "—".
  const bookingCountValue = finiteOrNull(bookingCount?.booking_count);
  const bookingsValue =
    bookingCountValue !== null ? String(bookingCountValue) : PLACEHOLDER;

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 2 }}>
      {/* The app shell supplies the "Revenue" page title, so the content does
          NOT repeat it (ui-quality.md). The in-content heading is the facility
          name; the period control sits alongside it. */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography component="h1" sx={{ fontWeight: 700 }} variant="h5">
            {facility?.name ?? "Your facility"}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            UTC reporting dates for the selected window
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
        >
          <PeriodSelector onChange={setPeriod} value={period} />
          <Button
            disabled={exporting}
            onClick={() => void handleExport()}
            size="small"
            startIcon={<DownloadOutlinedIcon />}
            type="button"
            variant="outlined"
          >
            Export transactions
          </Button>
        </Stack>
      </Stack>

      {exportError ? <Alert severity="error">{exportError}</Alert> : null}

      {revenueError ? (
        <Alert severity="error">
          We couldn&apos;t load revenue. {revenueError}
        </Alert>
      ) : null}

      {/* KPI strip — every value from a backend field; Avg/Day is the only
          derived figure (revenue ÷ days). */}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
        }}
      >
        <Kpi
          icon={AttachMoneyIcon}
          label="Period Revenue"
          value={loading ? PLACEHOLDER : periodRevenue}
        />
        <Kpi
          icon={CalendarMonthOutlinedIcon}
          label="Bookings"
          value={loading ? PLACEHOLDER : bookingsValue}
        />
        {/* "Avg/Day" (not "Avg Revenue/Day") so the only "Revenue"-bearing label
            on the page is the Period Revenue KPI — one source of truth per
            metric. It sits beside Period Revenue with a money value, so the
            "per-day average revenue" meaning is unambiguous. */}
        <Kpi
          icon={TrendingUpIcon}
          label="Avg/Day"
          value={loading ? PLACEHOLDER : avgPerDay}
        />
      </Box>

      {/* Daily earnings bar chart from getFacilityTrends.data_points. Titled
          "Daily earnings" (not "…revenue") so the only "Revenue"-bearing label on
          the page is the Period Revenue KPI — one source of truth per metric. */}
      <Paper component="section" sx={{ overflow: "hidden" }} variant="outlined">
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
          <Typography component="h2" variant="h6">
            Daily earnings
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
          {trendsError ? (
            <Alert severity="error">
              We couldn&apos;t load daily earnings. {trendsError}
            </Alert>
          ) : loading ? (
            <Stack
              role="status"
              spacing={1}
              sx={{
                alignItems: "center",
                justifyContent: "center",
                minHeight: 192,
              }}
            >
              <CircularProgress aria-hidden size={24} />
              <Typography color="text.secondary" variant="body2">
                Loading…
              </Typography>
            </Stack>
          ) : barData.length === 0 ? (
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "center",
                minHeight: 192,
              }}
            >
              <Typography color="text.secondary" variant="body2">
                No earnings in this window.
              </Typography>
            </Stack>
          ) : (
            <RevenueBarChart
              data={barData}
              formatValue={(v) => moneyLabel(v, trends?.currency)}
              seriesLabel="Earnings"
            />
          )}
        </Box>
      </Paper>
      <Paper component="section" variant="outlined" sx={{ overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography component="h2" variant="h6">
            Revenue by service
          </Typography>
        </Box>
        {sourcesError ? (
          <Alert severity="error">
            We couldn&apos;t load revenue by service. Use settlements for
            per-currency totals or export transactions.
          </Alert>
        ) : loading ? (
          <Box sx={{ p: 2 }} role="status">
            Loading service revenue…
          </Box>
        ) : !sources?.sources.length ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No settled payments or refunds in this period.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table
                size="small"
                aria-label="Revenue by service"
                sx={{ minWidth: 680 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell align="right">Gross revenue</TableCell>
                    <TableCell align="right">Refunds</TableCell>
                    <TableCell align="right">Platform fees</TableCell>
                    <TableCell align="right">Net before processing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sources.sources.map((source) => (
                    <TableRow key={source.source}>
                      <TableCell component="th" scope="row">
                        {SOURCE_LABELS[source.source] ?? "Other"}
                      </TableCell>
                      <TableCell align="right">
                        {moneyLabel(source.revenue, sources.currency)}
                      </TableCell>
                      <TableCell align="right">
                        {moneyLabel(source.refunded_amount, sources.currency)}
                      </TableCell>
                      <TableCell align="right">
                        {moneyLabel(source.platform_fees, sources.currency)}
                      </TableCell>
                      <TableCell align="right">
                        {moneyLabel(
                          source.net_before_processing,
                          sources.currency,
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ "& > *": { fontWeight: 700 } }}>
                    <TableCell component="th" scope="row">
                      Total
                    </TableCell>
                    <TableCell align="right">
                      {moneyLabel(sources.revenue, sources.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {moneyLabel(sources.refunded_amount, sources.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {moneyLabel(sources.platform_fees, sources.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {moneyLabel(
                        sources.net_before_processing,
                        sources.currency,
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="caption"
              color="text.secondary"
              component="p"
              sx={{ px: 2, py: 1.5 }}
            >
              Processing fees are unavailable. Net excludes these costs and is
              not a bank payout.
            </Typography>
          </>
        )}
      </Paper>
    </Stack>
  );
}
